import React from "react";

import {
	type CustomEmoji,
	Emoji as EMEmoji,
	emojiIndex,
	type EmojiProps,
	Picker as EMPicker,
	type PickerProps,
} from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import { EventEmitter } from "events";
import { memoize } from "lodash";

import WebsocketClient, { GroupEmitter } from "~/lib/websocket";

import ThemeContext from "./ThemeContext";

export type AvailableEmojiType = {
	name: string;
	url: string;
	aliasOf?: string;
};
// Wrapper around Emoji Picker to allow for custom emoji selection
export function Picker({ customOnly, accountId, ...props }: PickerProps & { customOnly?: boolean; accountId: string }) {
	let customEmojiList: CustomEmoji[] = [];
	const { isDarkMode } = React.useContext(ThemeContext);
	if (accountId) {
		customEmojiList = CustomEmojiManager.getInstance().getEmojisList(accountId);
	}
	return (
		<EMPicker
			darkMode={isDarkMode}
			custom={customEmojiList}
			{...props}
			include={customOnly ? ["custom"] : props.include}
		/>
	);
}

export function Emoji({
	accountId,
	fallbackUUID,
	...props
}: EmojiProps & { accountId: string; fallbackUUID?: string }) {
	const { emoji, ...rest } = props;
	const fallbackFn = React.useCallback(() => {
		return <span style={{ fontSize: "14px" }}>{emoji}</span>;
	}, [emoji]);
	let customEmojiMap: { [key: string]: CustomEmoji } = {};
	if (accountId) {
		customEmojiMap = CustomEmojiManager.getInstance().getEmojiMapForAccount(accountId);
	}
	if (typeof emoji === "string" && !isEmojiExist(emoji, accountId)) {
		if (fallbackUUID) {
			const fallbackCustomEmoji: CustomEmoji = {
				id: fallbackUUID,
				name: fallbackUUID,
				// eslint-disable-next-line camelcase
				short_names: [fallbackUUID],
				imageUrl: `${window.EMOJI_BASE_URL}/${fallbackUUID}?timestamp=${Math.floor(Date.now() / 60_000) * 60_000}`,
			};
			// @ts-ignore
			return <EMEmoji emoji={fallbackCustomEmoji} {...rest} fallback={fallbackFn} />;
		}
		return <span style={{ fontSize: "14px" }}>{emoji}</span>;
	}
	return (
		<EMEmoji
			emoji={typeof emoji === "string" ? (customEmojiMap[emoji.split(":").join("")] ?? emoji) : emoji}
			{...rest}
		/>
	);
}

export const isBuiltInEmoji = memoize((emojiName: string): boolean => {
	const skinToneMarkerIndex = emojiName.indexOf("::skin-tone-");
	if (skinToneMarkerIndex !== -1) {
		const emojiNameWithoutSkinTone = emojiName.substring(0, skinToneMarkerIndex + 1);
		return isBuiltInEmoji(emojiNameWithoutSkinTone);
	}
	const colonEscaped = emojiName.split(":").join("");
	const searchResult = emojiIndex.search(colonEscaped);
	if (!searchResult || searchResult.length === 0) return false;
	return searchResult.some(emoji => emoji.id === colonEscaped);
});
export function isEmojiExist(emojiName: string, accountId: string) {
	return isBuiltInEmoji(emojiName) || CustomEmojiManager.getInstance().has(emojiName, accountId);
}

export function getOriginalAccountId(): string {
	const value = CustomEmojiManager.getInstance().originalAccountId;
	if (!value) {
		throw new Error("getOriginalAccountId called before the CustomEmojiManager is initialized");
	}
	return value;
}

type EmojiSocketMessageType = "EMOJI_ADDED" | "EMOJI_DELETED";

type EmojiSocketMessageListener = ({
	type,
	data,
}: {
	type: EmojiSocketMessageType;
	data: { emoji: AvailableEmojiType };
}) => void;
export class CustomEmojiManager {
	// The id of the workspace that the user is from. When socket message comes from the same id,
	// we update the available emojis table so that user can use them immediately, if socket message
	// comes from external workspaces, we skip this update part and only trigger the listeners
	// Also used as a flag to see if the manager is initialized
	public originalAccountId?: string;

	private static WebSocketGroupPrefix = "custom_emoji";
	private json: { [accoundId: string]: { emojis: AvailableEmojiType[] } } = {};
	// a json representation of the original emojis data which is in string

	// _emojisList and _emojisMap contains cached data
	private _emojisList: { [accoundId: string]: CustomEmoji[] | null } = {};
	private _emojisMap: { [accountId: string]: { [key: string]: CustomEmoji } | null } = {};
	private eventEmitter: EventEmitter = new EventEmitter();
	private sockets: { [accountId: string]: GroupEmitter } = {};
	private static instance: CustomEmojiManager;
	private socketMessageListners: { [key: string]: EmojiSocketMessageListener } = {};

	public async refreshDatabase() {
		const originalAccountId = this.originalAccountId;
		if (!originalAccountId) {
			console.warn("No original account id");
			return;
		}
		fetch("/customEmojis.json", {
			headers: {
				"x-client-session-id": window.CLIENT_SESSION_ID,
				"x-requested-with": "XMLHttpRequest",
			},
		})
			.then(res => {
				return res.json();
			})
			.then((data: AvailableEmojiType[]) => {
				this.createOrUpdate(data, originalAccountId);
			});
	}

	private makeEmojiSocketMessageListenerForAccount = (accountId: string) => {
		if (accountId in this.socketMessageListners) {
			return this.socketMessageListners[accountId];
		}
		const listener = ({ type, data }: { type: EmojiSocketMessageType; data: { emoji: AvailableEmojiType } }) => {
			if (type === "EMOJI_ADDED") {
				this.addEmoji(data.emoji, accountId);
				this.eventEmitter.emit("emoji_added", data.emoji);
				return;
			}
			if (type === "EMOJI_DELETED") {
				this.deleteEmoji(data.emoji, accountId);
				this.eventEmitter.emit("emoji_deleted", data.emoji);
				return;
			}
		};
		this.socketMessageListners[accountId] = listener;
		return listener;
	};

	private onSocketClose = (accountId: string) => () => {
		// Currently when the custom emoji nodes are destroyed, the related listeners are removed at the sametime.
		// Since the socket group of the original workspace has a lonnger life cycle than the ones of the external workspace,
		// we can remove all listeners when that socket is closed, just in case some of the listeners are left behind.
		if (accountId !== this.originalAccountId) return;
		this.eventEmitter.removeAllListeners("emoji_added");
		this.eventEmitter.removeAllListeners("emoji_deleted");
	};

	private addEmoji(emoji: AvailableEmojiType, accountId: string) {
		if (!this.json[accountId]) {
			console.warn(`Custome emojis namespace '${accountId}' doesn't exist.`);
			return;
		}
		this.json[accountId].emojis.push(emoji);
		const customEmoji = {
			id: emoji.name,
			name: emoji.name,
			// eslint-disable-next-line camelcase
			short_names: [emoji.name],
			imageUrl: emoji.url,
		};

		this._emojisList[accountId]?.push(customEmoji);

		const accountEmojisMap = this._emojisMap[accountId];
		if (accountEmojisMap) {
			accountEmojisMap[emoji.name] = customEmoji;
			if (emoji.aliasOf && accountEmojisMap[emoji.aliasOf]) {
				!accountEmojisMap[emoji.aliasOf].short_names.includes(emoji.name) &&
					accountEmojisMap[emoji.aliasOf].short_names.push(emoji.name);
			}
		}
	}

	private deleteAliasRecursively(emojiName: string, accountId: string) {
		const nameSpacedMap = this._emojisMap[accountId];
		if (nameSpacedMap) {
			const emoji = nameSpacedMap[emojiName];
			if (!emoji) return;
			const aliases = emoji.short_names;
			aliases.forEach(alias => {
				if (alias === emojiName) return;
				this.deleteAliasRecursively(alias, accountId);
			});
			delete nameSpacedMap[emojiName];
		}
	}

	private deleteEmoji(emoji: AvailableEmojiType, accountId: string) {
		if (!this.json[accountId]) {
			console.warn(`Custome emojis namespace '${accountId}' doesn't exist.`);
			return;
		}
		this.json[accountId].emojis = this.json[accountId].emojis.filter(e => e.name !== emoji.name);
		this._emojisList[accountId] = null; // recreate the list when needed

		const accountEmojisMap = this._emojisMap[accountId];
		if (accountEmojisMap) {
			this.deleteAliasRecursively(emoji.name, accountId);
			if (emoji.aliasOf && accountEmojisMap[emoji.aliasOf]) {
				const newShortnames = accountEmojisMap[emoji.aliasOf].short_names.filter(name => name !== emoji.name);
				// eslint-disable-next-line camelcase
				accountEmojisMap[emoji.aliasOf].short_names = newShortnames;
			}
			delete accountEmojisMap[emoji.name];
		}
	}

	static getInstance() {
		if (!CustomEmojiManager.instance) {
			CustomEmojiManager.instance = new CustomEmojiManager();
		}
		return CustomEmojiManager.instance;
	}

	// Join a websocket group with the account id to get the updates of the emojis in that account
	joinGroup(accountId: string, metadata?: object) {
		if (this.sockets[accountId]) {
			console.warn(`Client already in group '${CustomEmojiManager.WebSocketGroupPrefix}.${accountId}'`);
			return;
		}
		this.sockets[accountId] = WebsocketClient.getInstance().getOrJoinGroup(
			`${CustomEmojiManager.WebSocketGroupPrefix}.${accountId.split("=").join("")}`,
			metadata,
		);
		this.sockets[accountId].on("message", this.makeEmojiSocketMessageListenerForAccount(accountId));
		this.sockets[accountId].on("close", this.onSocketClose(accountId));
		return CustomEmojiManager.instance;
	}

	// Leave the websocket group by account id
	leaveGroup(accountId: string) {
		if (this.sockets[accountId]) {
			this.sockets[accountId].off("message", this.makeEmojiSocketMessageListenerForAccount(accountId));
		}
		return CustomEmojiManager.instance;
	}

	// Initializing the singleton with 4 steps:
	// 	 1. Set the 'originalAccountId' to the id of the current user's account
	// 	 2. Set the 'enabledAccountId' to the 'originalAccountId', the default enabled namespace
	//	 3. Build up the 'json' object of the user's account from the emojis string
	//   4. Join the websocket group with the passed in account id
	init(emojis: AvailableEmojiType[], accountId: string, metadata?: object) {
		this.originalAccountId = accountId;
		this.json[accountId] = { emojis };
		this.joinGroup(accountId, metadata);
		return CustomEmojiManager.instance;
	}

	onEmojiAdded = (callback: (emoji: AvailableEmojiType) => void) => {
		this.eventEmitter.addListener("emoji_added", callback);
		return callback;
	};

	onEmojiDeleted = (callback: (emoji: AvailableEmojiType) => void) => {
		this.eventEmitter.addListener("emoji_deleted", callback);
		return callback;
	};

	removeListener = (eventName: "emoji_added" | "emoji_deleted", listener: (emoji: AvailableEmojiType) => void) => {
		this.eventEmitter.removeListener(eventName, listener);
	};

	// Get the emojis map under the current enabled namespace
	getEmojiMapForAccount(accountId: string): { [key: string]: CustomEmoji } {
		const cachedEmojiMap = this._emojisMap[accountId];
		if (cachedEmojiMap) {
			return cachedEmojiMap;
		}

		const json = this.json[accountId];
		if (!json) {
			console.warn("Custome emojis namespace doesn't exist.");
			return {};
		}
		const emojiDict: {
			[key: string]: CustomEmoji;
		} = {};

		const aliasEmoji: AvailableEmojiType[] = [];
		json.emojis.forEach(emoji => {
			emojiDict[emoji.name] = {
				id: emoji.name,
				name: emoji.name,
				// eslint-disable-next-line camelcase
				short_names: [emoji.name],
				imageUrl: emoji.url,
			};
			if (emoji.aliasOf) {
				aliasEmoji.push(emoji);
			}
		});
		aliasEmoji.forEach(emoji => {
			emojiDict[emoji.aliasOf as string]?.short_names.push(emoji.name);
		});
		this._emojisMap[accountId] = emojiDict;
		return emojiDict;
	}

	// Get the emojis list under the current enabled namespace
	getEmojisList(accountId: string) {
		const accoundEmojisList = this._emojisList[accountId];
		if (accoundEmojisList) {
			return accoundEmojisList;
		}

		const res = Object.values(this.getEmojiMapForAccount(accountId));
		this._emojisList[accountId] = res;
		return res;
	}

	// Create or update the emojis under a namespace, from the emojis string.
	// Calling this will build up the json representaion of the string
	// and clear the emojis map and list under that namespace
	createOrUpdate(emojis: AvailableEmojiType[], accoundId: string) {
		this.json[accoundId] = { emojis };
		this._emojisMap[accoundId] = null;
		this._emojisList[accoundId] = null;
		return CustomEmojiManager.instance;
	}

	getSrc(emojiColon: string, accountId: string) {
		const emojiName = emojiColon.split(":").join("");
		const emojiMap = this.getEmojiMapForAccount(accountId);
		if (emojiName in emojiMap) {
			return emojiMap[emojiName].imageUrl;
		}
		return null;
	}

	has(emojiName: string, accountId: string) {
		return emojiName.split(":").join("") in this.getEmojiMapForAccount(accountId);
	}
}

export const getEmojiUuidFromUrl = (url: string) => {
	if (!url) return null;
	const urlSplitted = url.split("/");
	if (!urlSplitted.length) return null;
	return urlSplitted.pop();
};

export function transformCustomEmojiFromHTML(html: string) {
	const doc = new DOMParser().parseFromString(html, "text/html");
	const customEmojis = doc.querySelectorAll("span.prose-custom-emoji");
	customEmojis.forEach(el => {
		const emojiId = el.getAttribute("data-id");
		const emojiUuid = el.getAttribute("data-uuid");
		if (!emojiId || !emojiUuid) return;
		const emojiSrc = `${window.EMOJI_BASE_URL}/${emojiUuid}`;
		if (emojiSrc) {
			el.outerHTML = `<img
				class="prose-custom-emoji"
				src=${emojiSrc}
				data-id=${emojiId}
				alt=${emojiId}
				data-uuid=${emojiUuid}
			/>`;
		}
	});
	const transformedHTML = doc.body.innerHTML;
	return transformedHTML;
}
